import {combineLatest} from 'rxjs';
import {map, startWith} from 'rxjs/operators';
import {useState, useEffect} from 'react';
import {Transaction, Query} from 'olympe';
import EPRID from '../models/EPRId';

/**
 * Combine props together in an object, combine also the current language
 *
 * @param {![{name: string, prop: Property, defaultValue: *}]} props
 * @return {*}
 */
export function combineProps(props) {
    const names = props.map((prop) => prop.name);
    const sources = props.map((prop) => {
        let obs = prop.prop.observe();
        // TODO remove defaultValue once bug with number defaultValue is fixed
        if (prop.defaultValue !== undefined) {
            obs = obs.pipe(startWith(prop.defaultValue));
        }
        return obs;
    });
    return combineLatest(sources).pipe(map((values) => {
        let i = 0;
        return values.reduce((acc, cur) => ({...acc, [names[i++]]: cur}), {});
    }));
}

/**
 * Create events usable by React component
 *
 * @param {![{name: string, event: Event, before: function(*, *, *)}]} events
 * @return {*}
 */
export function createEvents(events) {
    const setControlFlow = (event) => event.set(Date.now());
    return events.reduce((acc, cur) => ({...acc, [cur.name]: (a, v, t) => {
        if (cur.before) cur.before(a, v, t); setControlFlow(cur.event);
    }}), {});
}

/**
 * @Template T
 * @param {BrickContext} $
 * @param {string} prop
 * @param {T} defaultValue
 * @return {T}
 */
export function usePropertyDefault($, prop, defaultValue) {
    const [value, setValue] = useState();
    useEffect(() => {
        const sub = $.observe(prop, false).subscribe((v) => setValue(v === null ? defaultValue : v));
        return () => sub.unsubscribe();
    }, []);
    return value;
}

/**
 * Returns a JSON as an Object, with a default value
 *
 * @param {string} json
 * @param {Object} defaultValue
 * @return {{}}
 */
export function getObjectFromJSON(json, defaultValue = {}) {
    let style = defaultValue;
    try {
        style = JSON.parse(json);
    } catch (e) {}
    return style ? style : defaultValue;
}

/**
 * @typedef {{label: string, value: string}} LabelValue
 */

/**
 * Return the value of an item in an array of {LabelValue}
 * @param {[]} options
 * @param {string} label
 * @return {*|string}
 */
export const valueFromLabel = (options, label) => {
    const option = options.find((o) => o.label === label);
    return option ? option.value : '';
};

/**
 * List the account type possibilities
 * @type {Array<LabelValue>}
 */
export const accountTypeOptions = [
    {label: 'WBS / Project', value: 'P'},
    {label: 'Cost Center', value: 'K'},
    {label: 'Profit Center', value: '2'},
    {label: 'Statistical WBS', value: 'S'},
    {label: 'Blank (no account assignment needed)', value: ''},
    {label: 'Unknown', value: 'U'},
];

export const complianceIndicatorOptions = [
    'P - Compliant',
    'X - Not-compliant',
];

/**
 * Check whether the account contains an "empty" value
 * @param {Object | string} labelOrValue
 * @return {boolean}
 */
export const accountIsBlankOrUnknown = (labelOrValue) => {
    if (typeof labelOrValue === 'object') {
        return labelOrValue.value === '' || labelOrValue.value === 'U';
    } else {
        return labelOrValue === '' || labelOrValue === 'U';
    }
};

/**
 * Return the LabelValue corresponding to the given accountType
 * @param {string} accountType The accountType value
 * @return {LabelValue}
 */
export const matchAccountType = (accountType) => {
    return accountTypeOptions.find((label) => label.value === accountType);
};

/**
 * Return the LabelValue corresponding to the given accountType
 * @param {string} accountType The accountType value
 * @return {LabelValue}
 */
export const matchAccountTypeByLabel = (accountType) => {
    return accountTypeOptions.find((account) => account.label === accountType.label);
};

/**
 * List of the way of buying possibilities
 * @type {Array<LabelValue, LabelValue, LabelValue>}
 */
export const wayOfBuyingOptions = [
    {label: 'Standard Requisition', value: 'NB'},
    {label: 'Framework Requisition', value: 'FO'},
    {label: 'Capex Requisition', value: 'Z004'},
];

/**
 * List of default topics for the CB attachments
 * @type {string[]}
 */
export const defaultCompetitiveBiddingTopicOptions = [
    'Offer no.1 (selected supplier)',
    'Offer no.2',
    'Offer no.3',
    'Event summary',
];

/**
 * List of the exceptions for CP justification
 * @type {string[]}
 */
export const exceptionalCompetitiveBiddingTopicOptions = [
    'Exception',
];

/**
 * List of the exception possibilities
 * @type {Array<string>}
 */
export const exceptionOptions = [
    'Contract in place',
    'Single Source',
    'Emergency case',
];

/**
 * List of Item Category possibilities
 * @type {Array<LabelValue>}
 */
export const itemCategoryOptions = [{label: 'B - Limit', value: 'B'}];

/**
 * List of Status Name options
 * @type {string[]}
 */
export const statusNameOptions = [
    'Validated',
    'SoleSource',
    'Mandatory',
    'Blank',
];

/**
 * React hook to update a value based on a delay (debounced)
 *
 * @param {string} value
 * @param {number} delay - in milliseconds
 *
 * @return {string}
 */
export const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);
        return () => {
            clearTimeout(timer);
        };
    }, [value, delay]);
    return debouncedValue;
};

export const deleteOlympeObjectFromTag = (tag) => {
    new Transaction()
        .delete(tag)
        .execute()
        .catch(console.error);
};

/**
 * The main orange color which is used everywhere in this app
 * @type {string}
 */
export const ezprOrange = '#F19002FF';
export const bgColorSpender = '#FFFEBB';
export const bgColorRequester = '#E1E9FF';
export const bgColorDisabled = '#e5e5e5';
export const txtColorDisabled = '#2e3237';

/**
 * List of default topics for the CMV attachments
 * @type {string[]}
 */
export const defaultCollectiveMedicalVendorTopicOptions = [
    'Banking Details',
    'Sponsorship request letter',
    'Speaker acceptance letter',
];

/**
 * React custom hook that can be used for setting and getting value from local storage
 * @param {string} key
 * @param {string} initialValue
 * @return {[(any),((value: (((prevState: (any)) => (any)) | any)) => void)]}
 */
export const useLocalStorage = (key, initialValue) => {
    const [value, setValue] = useState(JSON.parse(localStorage.getItem(key)) ?? initialValue);

    useEffect(() => {
        localStorage.setItem(key, JSON.stringify(value));
    }, [key, value]);

    return [value, setValue];
};

/**
 * This is a copy of the useProperty hook from olympe core, but with a setter
 * Allows to bind a context property to a stateful react value.
 *
 * @param {!BrickContext} $
 * @param {string} property
 * @param {boolean=} [waitForValue=true]
 * @return {*} a stateful value usable in JSX
 */
export function useOlympeProperty($, property, waitForValue = true) {
    const [value, setValue] = useState();
    useEffect(() => {
        const subscription = $.observe(property, waitForValue).subscribe(setValue);
        return () => {
            subscription.unsubscribe();
        };
    }, []);
    return [value, (val) => $.set(property, val)];
}

/**
 * Get the uniqueId for the newly create PR
 * This is also used for the copy functionality
 *
 * @param {!BrickContext} $
 * @return {*}
 */
export const getUniquePrId = async ($) => {
    const transaction = Transaction.from($);
    const transactionTag = EPRID.create(transaction);
    try {
        await Transaction.process($, transaction);
        const qr = await Query.from(transactionTag).execute();
        return qr.getFirst().getId();
    } catch (error) {
        console.error(error);
        throw new Error('Failed to get unique PR ID');
    }
};

/**
 * Capitalize the first letter in a string
 *
 * @param {string} string
 * @param {string} splitOperator
 * @param {boolean} skipFirst
 * @return {string}
 */
export const splitTextAndCapitalizeFirstLetter = (string, splitOperator, skipFirst) => {
    const splitedString = string.split(splitOperator);
    const stringArr = splitedString.map((item, index) => {
        if (index === 0 && skipFirst) {
            return item;
        } else {
            return capitalizeFirstLetter(item);
        }
    });
    return stringArr.join('');
};

/**
 * Capitalize the first letter in a string
 *
 * @param {string} string
 * @return {string}
 */
export const capitalizeFirstLetter = (string) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
};
